import { API_BASE_URL } from '../apiServices/api_routes';
import axiosInstance from '../apiServices/axios';
import { TENT_WS_TICKET } from '../apiServices/api_routes';

// #region agent log
const agentLog = (location, message, data, hypothesisId) => {
    fetch('http://127.0.0.1:7798/ingest/539ad776-defd-4048-9a51-bd9937645f01', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '9d55d5' },
        body: JSON.stringify({
            sessionId: '9d55d5',
            runId: 'pre-fix',
            hypothesisId,
            location,
            message,
            data,
            timestamp: Date.now(),
        }),
    }).catch(() => {});
};
let socketSeq = 0;
// #endregion

/**
 * Resolve WebSocket origin.
 * Locally HTTP API stays on runserver :8000; Channels WebSockets run on Daphne :8001.
 * Override with REACT_APP_WS_BASE_URL when needed (e.g. wss://api.example.com).
 */
const toWsBase = () => {
    const override = (process.env.REACT_APP_WS_BASE_URL || '').trim().replace(/\/$/, '');
    if (override) {
        if (override.startsWith('ws://') || override.startsWith('wss://')) return override;
        if (override.startsWith('https://')) return `wss://${override.slice('https://'.length)}`;
        if (override.startsWith('http://')) return `ws://${override.slice('http://'.length)}`;
        return override;
    }

    const base = (API_BASE_URL || '').replace(/\/$/, '');
    try {
        const url = new URL(base.startsWith('http') ? base : `http://${base || '127.0.0.1:8000'}`);
        const localHost = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
        if (localHost && (url.port === '8000' || url.port === '')) {
            return `ws://${url.hostname}:8001`;
        }
        if (url.protocol === 'https:') return `wss://${url.host}`;
        return `ws://${url.host}`;
    } catch {
        if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
            return `wss://${window.location.host}`;
        }
        return `ws://${typeof window !== 'undefined' ? window.location.host : 'localhost'}`;
    }
};

/**
 * Open a Tent chat websocket for the current project.
 * Returns { socket, subscribe, unsubscribe, close }.
 */
export async function openTentChatSocket(projectId, { onEvent, onOpen, onClose } = {}) {
    let data;
    try {
        const response = await axiosInstance.post(TENT_WS_TICKET(projectId));
        data = response.data;
    } catch (error) {
        // #region agent log
        agentLog(
            'tentChatSocket.js:ticket',
            'ws ticket failed',
            { status: error?.response?.status || null, message: error?.message || 'ticket failed' },
            'H2',
        );
        // #endregion
        throw error;
    }
    const ticket = data?.ticket;
    if (!ticket) {
        throw new Error('Could not open live chat connection');
    }
    const url = `${toWsBase()}/ws/tent/?ticket=${encodeURIComponent(ticket)}`;
    const socket = new WebSocket(url);
    // #region agent log
    const socketId = ++socketSeq;
    agentLog(
        'tentChatSocket.js:new',
        'socket created',
        { socketId, readyState: socket.readyState, visibility: document.visibilityState },
        'H2',
    );
    // #endregion

    socket.onopen = () => {
        // #region agent log
        agentLog('tentChatSocket.js:open', 'socket open', { socketId, visibility: document.visibilityState }, 'H1');
        // #endregion
        if (onOpen) onOpen();
    };
    socket.onclose = event => {
        // #region agent log
        agentLog(
            'tentChatSocket.js:close',
            'socket close',
            {
                socketId,
                code: event?.code ?? null,
                wasClean: Boolean(event?.wasClean),
                reason: String(event?.reason || '').slice(0, 120),
                visibility: document.visibilityState,
            },
            'H1',
        );
        // #endregion
        if (onClose) onClose(event);
    };
    socket.onerror = () => {
        // #region agent log
        agentLog('tentChatSocket.js:error', 'socket error', { socketId, readyState: socket.readyState }, 'H4');
        // #endregion
    };
    socket.onmessage = event => {
        try {
            const payload = JSON.parse(event.data);
            if (onEvent) onEvent(payload);
        } catch (error) {
            // #region agent log
            agentLog(
                'tentChatSocket.js:onmessage',
                'socket handler threw',
                {
                    socketId,
                    message: String(error?.message || 'handler threw').slice(0, 240),
                    stack: String(error?.stack || '').slice(0, 500),
                },
                'H3',
            );
            // #endregion
        }
    };

    const send = action => {
        if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(action));
        }
    };

    return {
        socket,
        subscribe: conversationId => send({ action: 'subscribe', conversation_id: conversationId }),
        unsubscribe: conversationId => send({ action: 'unsubscribe', conversation_id: conversationId }),
        close: () => {
            try {
                socket.close();
            } catch {
                // ignore
            }
        },
    };
}
